/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { Avatar } from "../../shared/Avatar";
import type { RotaWarning } from "../engine/rules";
import type { Shift } from "./types";
import { warningText } from "./warningText";

type Props = { shift: Shift; warnings: RotaWarning[]; onOpen: () => void };

// One slot in the grid: the person and role, or a yellow "Unassigned".
// Drafts have a dashed border; a triangle marks rule warnings.
export const ShiftCard = defineComponent<Props>({
  name: "ShiftCard",
  props: ["shift", "warnings", "onOpen"],
  setup(props) {
    const { t } = useLocalization();
    return () => {
      const s = props.shift;
      const rules = props.warnings.filter((w) => w.kind !== "not_published");
      const open = s.staff_id == null;
      return (
        <button
          class={[
            "group flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left text-xs transition hover:shadow",
            open ? "border-warning/60 bg-warning/15" : "bg-base-100",
            s.status === "draft" ? "border-dashed" : open ? "" : "border-base-300",
            rules.length && !open ? "border-error/60" : "",
          ].join(" ")}
          title={rules.map((w) => warningText(w, t)).join("\n") || undefined}
          onClick={() => props.onOpen()}
        >
          {open ? (
            <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-warning text-warning">
              <span class="material-symbols-outlined text-sm">person_add</span>
            </span>
          ) : (
            <Avatar name={s.staff_name ?? "?"} id={s.staff_id!} color={s.staff_color} size="xs" />
          )}
          <span class="min-w-0 flex-1">
            <span class={["block truncate font-medium", open ? "text-warning" : ""].join(" ")}>
              {open ? t("workday.rota.unassigned") : s.staff_name}
            </span>
            <span class="block truncate text-[11px] text-base-content/60">{s.role_name ?? "—"}</span>
          </span>
          {s.source === "auto" && s.status === "draft" && !rules.length && (
            <span class="material-symbols-outlined text-sm text-primary/60" title={t("workday.auto.by_auto")}>auto_awesome</span>
          )}
          {rules.length > 0 && <span class="material-symbols-outlined text-sm text-error">warning</span>}
        </button>
      );
    };
  },
});
